import { EventClickArg, EventDropArg, EventSourceInput } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import FullCalendar from '@fullcalendar/react';
import scrollgridPlugin from '@fullcalendar/scrollgrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Appointment from '@spike/appointment-model';
import { BlockCalendar } from '@spike/block-calendar-model';
import { MarketplaceSchedule } from '@spike/model';
import clsx from 'clsx';
import ActionNotSupportedDialog from 'components/CreateBooking/UI/ActionNotSupportedDialog';
import { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    convertToBlockCalendarEvent,
    convertToEvent,
    convertToSlotGroupEvents,
    convertToTempEvent,
    getWeekBusinessHours,
    renderEvent
} from '../FullCalendar/FullCalendarUtils';
import NowIndicatorFullCalendar from '../FullCalendar/NowIndicatorFullCalendar';
import { useFullCalendarStyles } from '../FullCalendar/StylesFullCalendar';
import { SlotGroup, TempNewBooking } from '../FullCalendar/model';
import DayHeaderFullCalendar from './DayHeaderFullCalendar';
import { usePermissions } from 'hooks';

interface WeekFullCalendarProps {
    from: Moment;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    showPetsCountBadge: boolean;
    staffs: Array<Staff>;
    appointments: Array<Appointment>;
    blocks: Array<BlockCalendar>;
    selectedStaffIds?: Array<number>;
    multipleBookingsIds: Array<number>;
    tempNewBooking?: TempNewBooking;
    shrink: boolean;
    isSlotsView: boolean;
    businessArea: BusinessAreaType;
    readOnly: boolean;
    onClickAppointment?: (id: number) => void;
    onClickBlockCalendar?: (id: number) => void;
    onRescheduled?: (appointmentId: number, at: Moment, revert: () => void) => void;
    onRescheduledBlocked?: (blocked: BlockCalendar, revert: () => void) => void;
    onBook?: (at: Moment) => void;
    onScroll: () => void;
    onClickSlotGroup?: (slotGroup: SlotGroup, slotElement: HTMLElement) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            'width': '100%',
            '& .fc .fc-timegrid-slot-label': {
                border: 'none !important'
            },
            '& .fc-bg-event': {
                opacity: 1
            },
            '& .fc-scroller .fc-timegrid-slot-label': {
                [theme.breakpoints.down('sm')]: {
                    height: '21px !important'
                },
                [theme.breakpoints.only('md')]: {
                    height: '33px !important'
                },
                [theme.breakpoints.only('lg')]: {
                    height: '42px !important'
                },
                [theme.breakpoints.only('xl')]: {
                    height: '49px !important'
                }
            },
            '& .fc-license-message': {
                display: 'none !important'
            },
            '& .fc-scrollgrid-section-body .fc-scroller': {
                'scrollbarWidth': 'none',
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            },
            [theme.breakpoints.down('sm')]: {
                '& .fc-timegrid-cols td.fc-timegrid-col.fc-day': {
                    position: 'relative'
                },
                '& .fc-timegrid-cols td.fc-timegrid-col.fc-day > .fc-timegrid-col-frame': {
                    position: 'absolute !important',
                    top: '0 !important',
                    right: '0 !important',
                    bottom: '0 !important',
                    left: '0 !important',
                    width: 'auto !important',
                    height: 'auto !important'
                },
                '& .fc-timegrid-cols .fc-timegrid-event-harness': {
                    left: '0 !important',
                    right: '0 !important'
                }
            }
        },
        slotsViewContainer: {
            '& .fc-timegrid-event-harness-inset': {
                left: '0px !important',
                right: '0px !important'
            },
            '& .fc .fc-timegrid-slot': {
                height: '32px !important',

                [theme.breakpoints.up('md')]: {
                    height: '44px !important'
                }
            }
        },
        dayHeader: {
            'backgroundColor': 'white',
            'borderTop': 'none !important',
            'borderLeft': 'none !important',
            'borderRight': 'none !important',
            'borderBottom': '1px solid #D8D8D8 !important',
            'verticalAlign': 'middle !important',
            [theme.breakpoints.down('sm')]: {
                height: '32px'
            },
            [theme.breakpoints.only('md')]: {
                height: '48px'
            },
            [theme.breakpoints.only('lg')]: {
                height: '60px'
            },
            [theme.breakpoints.only('xl')]: {
                height: '90px'
            },

            '& .fc-col-header-cell-cushion': {
                textDecoration: 'none'
            }
        },
        nowIndicator: {
            '&.fc-timegrid-now-indicator-arrow': {
                border: 'none',
                width: '100%'
            },
            '&.fc-timegrid-now-indicator-line': {
                'border': '1px solid black !important',
                'height': '0px',
                'backgroundColor': 'black',
                '&::before': {
                    content: '""',
                    borderRadius: '100%',
                    position: 'absolute',
                    height: '8px',
                    width: '8px',
                    background: 'black',
                    left: '-1px',
                    marginTop: '-4px',
                    boxShadow: 'inherit',
                    zIndex: 999
                }
            }
        }
    })
);

export const WeekFullCalendar: FunctionComponent<WeekFullCalendarProps> = props => {
    const classes = useStyles();
    const fullcalendarClasses = useFullCalendarStyles();
    const permissions = usePermissions();

    const today = moment().tz(props.timeZone);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const fullCalendarRef = createRef<FullCalendar>();

    const [showActionNotSupported, setShowActionNotSupported] = useState(false);
    const getCalendarNow = useCallback(() => moment.tz(props.timeZone).format(), [props.timeZone]);

    useEffect(() => {
        fullCalendarRef.current?.getApi().gotoDate(props.from.toDate());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.from]);

    useEffect(() => {
        props.tempNewBooking &&
            fullCalendarRef.current?.getApi().scrollToTime({ hour: props.tempNewBooking?.at.get('hour') });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tempNewBooking]);

    // Handle fullcalendar scroll event to close edit slot modal
    useEffect(() => {
        if (wrapperRef.current) {
            const scrollEl = wrapperRef.current.querySelector('.fc-scroller-liquid-absolute');

            if (scrollEl) {
                scrollEl.addEventListener('scroll', props.onScroll);

                return () => {
                    scrollEl.removeEventListener('scroll', props.onScroll);
                };
            }
        }
    }, []);

    const scrollTime = today.format('HH:00:00');

    const businessHours = useMemo(
        () => (props.schedule ? getWeekBusinessHours(props.from, props.schedule, props.timeZone) : []),
        [props.from, props.schedule, props.timeZone]
    );

    const events: EventSourceInput = useMemo(() => {
        if (props.isSlotsView) {
            const period = {
                from: props.from.clone(),
                to: props.from.clone().endOf('week')
            };

            const staffs =
                props.selectedStaffIds && props.selectedStaffIds.length > 0
                    ? props.staffs.filter(staff => props.selectedStaffIds?.includes(Number(staff.id)))
                    : props.staffs;

            return convertToSlotGroupEvents(staffs, period, props.timeZone);
        }

        const events = props.appointments
            .filter(appointment => {
                if (props.businessArea === SERVICES_BUSINESS_AREAS.GROOMING) {
                    return appointment.services.some(
                        service =>
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.DENTAL ||
                            service.businessArea?.id === SERVICES_BUSINESS_AREAS.GROOMING
                    );
                }

                return appointment.services.some(service => service.businessArea?.id === props.businessArea);
            })
            .map(appointment => convertToEvent(appointment, props.timeZone, 'weekly', props.multipleBookingsIds));

        const blocks = props.blocks.map(block => ({
            ...convertToBlockCalendarEvent(block, props.timeZone),
            rendering: 'background'
        }));

        return props.tempNewBooking
            ? [...events, ...blocks, convertToTempEvent(props.tempNewBooking, props.timeZone)]
            : [...events, ...blocks];
    }, [
        props.from,
        props.appointments,
        props.blocks,
        props.tempNewBooking,
        props.timeZone,
        props.multipleBookingsIds,
        props.isSlotsView,
        props.staffs,
        props.businessArea,
        props.selectedStaffIds
    ]);

    useEffect(() => {
        const root = wrapperRef.current;
        if (!root) {
            return;
        }

        const narrowQuery = window.matchMedia('(max-width: 959.95px)');

        const alignWeekColumns = () => {
            if (!narrowQuery.matches) {
                return;
            }

            const label = root.querySelector<HTMLElement>('.fc-timegrid-slot-label');
            if (!label) {
                return;
            }

            if (!props.isSlotsView) {
                const axisWidth = `${Math.ceil(label.getBoundingClientRect().width)}px`;
                root.querySelectorAll<HTMLElement>('.fc-timegrid-cols col, table.fc-col-header col').forEach(col => {
                    if (col.style.getPropertyValue('width') !== axisWidth) {
                        col.style.setProperty('width', axisWidth, 'important');
                    }
                });
            }

            root.querySelectorAll<HTMLElement>('.fc-timegrid-cols td.fc-timegrid-col.fc-day').forEach(day => {
                day.style.position = 'relative';
                const frame = day.querySelector<HTMLElement>(':scope > .fc-timegrid-col-frame');
                if (!frame) {
                    return;
                }
                frame.style.setProperty('position', 'absolute', 'important');
                frame.style.setProperty('top', '0', 'important');
                frame.style.setProperty('right', '0', 'important');
                frame.style.setProperty('bottom', '0', 'important');
                frame.style.setProperty('left', '0', 'important');
                frame.style.setProperty('width', 'auto', 'important');
                frame.style.setProperty('height', 'auto', 'important');
            });

            root.querySelectorAll<HTMLElement>('.fc-timegrid-event-harness').forEach(harness => {
                harness.style.setProperty('left', '0', 'important');
                harness.style.setProperty('right', '0', 'important');
            });
        };

        alignWeekColumns();
        const observer = new ResizeObserver(alignWeekColumns);
        observer.observe(root);
        narrowQuery.addEventListener('change', alignWeekColumns);

        return () => {
            observer.disconnect();
            narrowQuery.removeEventListener('change', alignWeekColumns);
        };
    }, [events, businessHours, props.isSlotsView]);

    const eventClickHandler = (args: EventClickArg) => {
        args.event.extendedProps.type === 'slot-group' &&
            props.onClickSlotGroup?.(args.event.extendedProps.slotGroup, args.el);

        args.event.extendedProps.type === 'appointment' &&
            props.onClickAppointment?.(args.event.extendedProps.appointmentId);

        args.event.extendedProps.type === 'block' &&
            permissions.editWorkSchedule &&
            props.onClickBlockCalendar?.(args.event.extendedProps.blockId);
    };

    const dropHandler = (args: EventDropArg, timeZone: string) => {
        if (args.event.extendedProps.type === 'block') {
            const blockedTime: BlockCalendar = props.blocks.find(
                block => block.id === args.event.extendedProps.blockId
            )!;
            blockedTime.start = moment(args.event.start).tz(props.timeZone);
            blockedTime.end = moment(args.event.end).tz(props.timeZone);
            props.onRescheduledBlocked && props.onRescheduledBlocked(blockedTime, args.revert);
        } else if (args.event.extendedProps.isMultipleBooking) {
            setShowActionNotSupported(true);
            args.revert();
        } else {
            props.onRescheduled &&
                props.onRescheduled(
                    args.event.extendedProps.appointmentId,
                    moment(args.event.start).tz(props.timeZone),
                    args.revert
                );
        }
    };

    const dateClickHandler = (args: DateClickArg, timeZone: string) => {
        const at = moment(args.date).tz(timeZone);
        const quarter = Math.trunc(at.minutes() / 15);
        at.minutes(quarter * 15);
        props.onBook && props.onBook(at);
    };

    const fullCalendarComponent = useMemo(
        () => (
            <FullCalendar
                ref={fullCalendarRef}
                plugins={[interactionPlugin, momentTimezonePlugin, timeGridPlugin, scrollgridPlugin].filter(
                    p => !props.readOnly || p.name !== interactionPlugin.name
                )}
                initialView="timeGridWeek"
                timeZone={props.timeZone}
                now={getCalendarNow}
                editable={!props.readOnly}
                eventDurationEditable={false}
                slotDuration="0:15:00"
                slotLabelInterval="1:00:00"
                snapDuration="0:10:00"
                expandRows={true}
                height="100%"
                headerToolbar={false}
                allDaySlot={false}
                stickyHeaderDates={false}
                dayHeaderContent={dayHeaderProps => (
                    <DayHeaderFullCalendar
                        staffs={props.staffs}
                        appointments={props.appointments}
                        selectedStaffIds={props.selectedStaffIds}
                        businessArea={props.businessArea}
                        timeZone={props.timeZone}
                        showPetsCountBadge={props.showPetsCountBadge}
                        {...dayHeaderProps}
                    />
                )}
                dayHeaderClassNames={[classes.dayHeader]}
                dayCellClassNames={
                    props.readOnly
                        ? [fullcalendarClasses.dayCell, fullcalendarClasses.notClickeable]
                        : [fullcalendarClasses.dayCell]
                }
                slotLabelFormat={{
                    hour: 'numeric',
                    omitZeroMinute: false
                }}
                slotLabelClassNames={[fullcalendarClasses.slotLabel]}
                slotLaneClassNames={
                    props.readOnly
                        ? [fullcalendarClasses.slotLane, fullcalendarClasses.notClickeable]
                        : [fullcalendarClasses.slotLane]
                }
                nowIndicator={true}
                nowIndicatorClassNames={[classes.nowIndicator]}
                nowIndicatorContent={args => (
                    <NowIndicatorFullCalendar
                        {...args}
                        timeZone={props.timeZone}
                    />
                )}
                scrollTime={scrollTime}
                events={events}
                eventContent={event => renderEvent(event, props.appointments)}
                eventClassNames={[props.isSlotsView ? fullcalendarClasses.slotEvent : fullcalendarClasses.event]}
                businessHours={businessHours}
                eventClick={eventClickHandler}
                eventDrop={args => dropHandler(args, props.timeZone)}
                dateClick={args => dateClickHandler(args, props.timeZone)}
                eventOrder={(a: any, b: any) => {
                    if (a.extendedProps.type === 'block' && b.extendedProps.type !== 'block') {
                        return -1;
                    } else if (a.extendedProps.type !== 'block' && b.extendedProps.type === 'block') {
                        return 1;
                    }
                    return 0;
                }}
                {...(props.isSlotsView ? { dayMinWidth: 150 } : {})}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [events, props.shrink, props.staffs, props.from, props.selectedStaffIds, props.timeZone, businessHours]
    );

    return (
        <>
            <div
                ref={wrapperRef}
                className={clsx(classes.container, props.isSlotsView && classes.slotsViewContainer)}
            >
                {fullCalendarComponent}
            </div>
            {showActionNotSupported && <ActionNotSupportedDialog onClose={() => setShowActionNotSupported(false)} />}
        </>
    );
};

export default WeekFullCalendar;
